const RELOAD_AT_KEY = 'chunk-load-reload-at';
const MIN_RELOAD_INTERVAL_MS = 60_000;

/**
 * A chunk request that 404s after a deploy (stale client asking for old
 * hashed chunk URLs) — the top error-tracking issue in PostHog (~1,100
 * users/month left with dead navigation). Requires `_next/static` in the
 * message so browser-extension chunk failures (safari-web-extension://…)
 * don't trigger reloads of our app.
 */
function isStaleChunkError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const { name, message } = err as { name?: unknown; message?: unknown };
  if (typeof message !== 'string' || !message.includes('_next/static')) {
    return false;
  }
  return (
    name === 'ChunkLoadError' ||
    /failed to load chunk|loading chunk .* failed/i.test(message)
  );
}

/**
 * Recover from stale-deploy chunk failures with a single hard reload, which
 * fetches the new deployment's HTML + chunk manifest. Throttled via
 * sessionStorage so a genuinely broken network can't cause a reload loop.
 * Returns a cleanup function for the effect that registers it.
 */
export function setupChunkLoadErrorRecovery(): () => void {
  const maybeReload = (err: unknown) => {
    if (!isStaleChunkError(err)) return;
    let lastReloadAt = 0;
    try {
      lastReloadAt = Number(sessionStorage.getItem(RELOAD_AT_KEY)) || 0;
    } catch {
      // sessionStorage unavailable — still reload, just without the throttle
    }
    if (Date.now() - lastReloadAt < MIN_RELOAD_INTERVAL_MS) return;
    try {
      sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
    } catch {
      /* noop */
    }
    window.location.reload();
  };

  const onError = (event: ErrorEvent) => maybeReload(event.error);
  const onRejection = (event: PromiseRejectionEvent) =>
    maybeReload(event.reason);

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
